import { type FieldValues } from 'react-hook-form';

import { FieldRenderer } from '../FieldRenderer/FieldRenderer';
import { ObjectFieldGroup } from '../ObjectFieldGroup/ObjectFieldGroup';
import { isFieldVisible } from '../hidden-fields/hidden-fields';
import { sectionGridClasses } from '../resolve-field-layout/resolve-field-layout';

import { type DynamicFormFieldsProps } from './DynamicFormFields.types';

/**
 * One section's grid of controls.
 *
 * **The columns answer to the CONTAINER, not to the window.** The grid opens one
 * column, then a second, third and fourth as the fields region grows past the
 * `@fits-*` widths — so a 960px dialog lays out three columns while the same
 * form in a narrow settings scope column lays out one. It was `sm:grid-cols-2`
 * before, which gave every form everywhere exactly two columns the moment the
 * *browser* passed 640px, whatever box the form had been dropped into.
 *
 * `visibleWhen` is evaluated against the whole form's values, subscribed once by
 * `DynamicForm` and passed down: with sections there are N grids, and N
 * whole-form subscriptions would be N re-renders per keystroke. Reading with
 * `getValues` instead would go stale — the exact defect P9's slug field shipped
 * with and then fixed.
 */
export function DynamicFormFields<TValues extends FieldValues>({
  fields,
  control,
  locale,
  columns,
  values,
  pathFor,
  sourcePathFor,
  onSlugManualEdit,
  onAfterChange,
  onParseFailureChange,
  className,
}: DynamicFormFieldsProps<TValues>) {
  const visibleFields = fields.filter((field) => isFieldVisible(field, values));

  return (
    <div
      // The ceiling this grid was given, so a measured walk can derive how many
      // columns the container SHOULD have opened instead of hardcoding a number
      // per surface.
      data-columns={String(columns)}
      className={sectionGridClasses(columns, className)}
    >
      {visibleFields.map((field) =>
        // An object is N controls, not one, so it never goes through a
        // `Controller`. The branch is here rather than inside `FieldRenderer`
        // because the group renders one more grid, and this component is the one
        // that already holds everything a grid needs.
        field.kind === 'object' ? (
          <ObjectFieldGroup
            key={field.name}
            field={field}
            control={control}
            columns={columns}
            locale={locale}
            values={values}
            pathFor={pathFor}
            sourcePathFor={sourcePathFor}
            onSlugManualEdit={onSlugManualEdit}
            onAfterChange={onAfterChange}
            onParseFailureChange={onParseFailureChange}
          />
        ) : (
          <FieldRenderer
            // Keyed by locale as well as name: switching the content language must
            // give the translatable controls fresh state, or the Arabic value
            // briefly shows under the English tab.
            key={`${field.name}:${field.isTranslatable === true ? locale : 'x'}`}
            field={field}
            path={pathFor(field, locale)}
            control={control}
            columns={columns}
            locale={locale}
            sourcePath={field.kind === 'slug' ? sourcePathFor(field, locale) : undefined}
            onSlugManualEdit={() => {
              onSlugManualEdit(field.name);
            }}
            onAfterChange={onAfterChange}
            onParseFailureChange={onParseFailureChange}
          />
        ),
      )}
    </div>
  );
}
